import React, { useEffect, useState, useMemo } from 'react';
import { Typography, Select, Table, Tag, Space, Empty, Spin, Tooltip, Switch } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api';

/**
 * Every AI-drafted evaluation for a form, pivoted so each question is a column.
 *
 * Built for reading down rather than across. "How did the AI score this call"
 * is already answerable on the case itself; what is not is "how does the AI
 * answer question 7, across forty calls" -- which is the view that shows
 * whether an instruction is landing, or whether a question is being read the
 * way it was meant.
 *
 * Each cell carries the whole answer: what was chosen, the points, the
 * comment, and the transcript it was drawn from. Splitting those across three
 * columns per question would triple the width and break the scan.
 */

function AnswerCell({ answer, showCited }) {
  if (!answer) return <Typography.Text type="secondary">—</Typography.Text>;

  const { option, points, comment, cited } = answer;
  return (
    <div style={{ fontSize: 12, lineHeight: 1.45 }}>
      <div style={{ marginBottom: 2 }}>
        {option
          ? <Tag color="blue" style={{ margin: 0, whiteSpace: 'normal' }}>{option}</Tag>
          : <Typography.Text type="secondary">no option</Typography.Text>}
        {points != null && points !== 0 && (
          <Typography.Text strong style={{ marginLeft: 6 }}>{points} pts</Typography.Text>
        )}
      </div>
      {comment && (
        <div style={{ color: '#262626', whiteSpace: 'pre-wrap' }}>{comment}</div>
      )}
      {showCited && cited && (
        // Monospace and dimmed: it is quoted transcript, not the AI's words,
        // and the [mm:ss] stamps line up so the passage can be found.
        <div style={{
          marginTop: 4, padding: '4px 6px', background: '#fafafa',
          border: '1px solid #f0f0f0', borderRadius: 3,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 11, color: '#8c8c8c', whiteSpace: 'pre-wrap',
          maxHeight: 120, overflowY: 'auto',
        }}>
          {cited}
        </div>
      )}
    </div>
  );
}

export default function EvalAIScoreAuditPage() {
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [forms, setForms]         = useState([]);
  const [campaignId, setCampaignId] = useState(null);
  const [formId, setFormId]       = useState(null);

  const [questions, setQuestions] = useState([]);
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [showCited, setShowCited] = useState(true);

  // Array.isArray on every one of these, not just a null guard.
  //
  // A 404 on this app does not fail: the errorhandler returns index.html, so a
  // wrong or renamed API path comes back as 200 with a page of HTML. Axios
  // resolves, the string lands in state, and the failure surfaces as
  // "map is not a function" somewhere unrelated -- which is exactly how this
  // page first broke, on a call to /api/campaigns that does not exist.
  const asArray = (d) => (Array.isArray(d) ? d : []);

  // The same source the Cases page uses. It returns the campaigns this user
  // can actually see, where the admin list is the whole table -- and offering
  // a filter for a campaign whose evaluations are then hidden is worse than
  // not offering it.
  useEffect(() => {
    api.get('/api/agent-stats/options')
      .then((r) => setCampaigns(asArray(r.data?.campaigns)))
      .catch(() => setCampaigns([]));
  }, []);

  // Forms follow the campaign, so the list only offers forms that campaign
  // actually uses. With no campaign chosen it falls back to every published
  // evaluation form.
  useEffect(() => {
    const params = { form_type: 'evaluation' };
    if (campaignId) params.campaign_id = campaignId;
    api.get('/api/eval-forms/published', { params })
      .then((r) => setForms(asArray(r.data)))
      .catch(() => setForms([]));
  }, [campaignId]);

  useEffect(() => {
    if (!formId) { setQuestions([]); setRows([]); return; }
    setLoading(true);
    api.get('/api/eval-ai-audit', { params: { form_id: formId, campaign_id: campaignId || undefined } })
      .then((r) => { setQuestions(asArray(r.data?.questions)); setRows(asArray(r.data?.rows)); })
      .catch(() => { setQuestions([]); setRows([]); })
      .finally(() => setLoading(false));
  }, [formId, campaignId]);

  const columns = useMemo(() => {
    const base = [
      {
        title: 'Evaluation', key: 'case', width: 150, fixed: 'left',
        render: (_, r) => (
          <div style={{ fontSize: 12 }}>
            <Typography.Link onClick={() => navigate(`/cases/${r.ID}`)}>#{r.ID}</Typography.Link>
            <div style={{ color: '#8c8c8c' }}>{r.AgentName || '—'}</div>
            <div style={{ color: '#8c8c8c' }}>{(r.CallDate || '').slice(0, 10)}</div>
            <Space size={4} wrap style={{ marginTop: 2 }}>
              {r.IsFeedbackOnly ? <Tag color="purple" style={{ margin: 0 }}>FB only</Tag> : null}
              {r.UseToTrainAI ? <Tag color="green" style={{ margin: 0 }}>Training</Tag> : null}
            </Space>
          </div>
        ),
      },
    ];

    // One column per question, labelled with its section so a long form stays
    // navigable when scrolled sideways.
    const qCols = questions.map((q) => ({
      title: (
        <Tooltip title={q.AIInstructions ? `AI instructions: ${q.AIInstructions}` : 'No AI instructions set'}>
          <div style={{ fontSize: 11, lineHeight: 1.3 }}>
            <div style={{ color: '#8c8c8c' }}>{q.SectionName}</div>
            <div>{q.QuestionText}</div>
            <div style={{ color: '#bfbfbf' }}>
              {q.MaxPoints > 0 ? `max ${q.MaxPoints}` : 'no points'}
              {q.AIInstructions ? ' · has AI notes' : ''}
              <InfoCircleOutlined style={{ marginLeft: 4, fontSize: 10, opacity: 0.45 }} />
            </div>
          </div>
        </Tooltip>
      ),
      key: `q${q.ID}`,
      width: 300,
      render: (_, r) => <AnswerCell answer={r.Answers?.[String(q.ID)]} showCited={showCited} />,
    }));

    return [...base, ...qCols];
  }, [questions, showCited, navigate]);

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0 }}>Eval AI Score Audit</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginTop: -8 }}>
        Every AI-drafted evaluation for a form, one question per column. Read down a
        column to see how the same question was answered across many calls.
      </Typography.Paragraph>

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="All campaigns"
          style={{ width: 240 }}
          value={campaignId}
          onChange={(v) => { setCampaignId(v ?? null); setFormId(null); }}
          options={campaigns.map((c) => ({ label: c.Name, value: c.ID }))}
        />
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Select a form"
          style={{ width: 320 }}
          value={formId}
          onChange={setFormId}
          options={forms.map((f) => ({ label: `${f.Name} (v${f.Version})`, value: f.ID }))}
        />
        <Space size={6}>
          <Switch size="small" checked={showCited} onChange={setShowCited} />
          <Typography.Text style={{ fontSize: 13 }}>Show transcript extracts</Typography.Text>
        </Space>
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : !formId ? (
        <Empty description="Select a form to audit" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : rows.length === 0 ? (
        <Empty description="No AI-drafted evaluations for this form yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Table
          rowKey="ID"
          dataSource={rows}
          columns={columns}
          size="small"
          bordered
          sticky
          scroll={{ x: 'max-content' }}
          pagination={{ defaultPageSize: 25, showSizeChanger: true, pageSizeOptions: ['10', '25', '50'], showTotal: (t) => `${t} evaluations` }}
        />
      )}
    </>
  );
}
